// .. /components/seatmaps/Basic.js

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Accessibility } from "lucide-react";

//import arena from "./arena.json"; // 1111
//import { seatMap as STATIC_SEATMAP } from "./papp-laszlo-budapest-sportarena.js"; //2222

function useDragPreventClick() {
    const preventClickRef = useRef(false);
    const start = useRef({ x: 0, y: 0 });
    const timer = useRef(null);

    useEffect(() => {
        return () => {
            if (timer.current) clearTimeout(timer.current);
        };
    }, []);

    const onPointerDown = useCallback((e) => {
        start.current = { x: e.clientX, y: e.clientY };
        preventClickRef.current = false;
    }, []);

    const onPointerUp = useCallback((e) => {
        const dx = Math.abs(e.clientX - start.current.x);
        const dy = Math.abs(e.clientY - start.current.y);

        if (dx > 5 || dy > 5) {
            preventClickRef.current = true;
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => {
                preventClickRef.current = false;
            }, 120);
        }
    }, []);

    return { preventClickRef, onPointerDown, onPointerUp };
}

function useIsTouchDevice() {
    const [isTouch, setIsTouch] = useState(false);

    useEffect(() => {
        setIsTouch(
            "ontouchstart" in window ||
            navigator.maxTouchPoints > 0 ||
            navigator.msMaxTouchPoints > 0
        );
    }, []);

    return isTouch;
}

const STATUS_CLASS = {
    "reserved-mine": "bg-yellow-300 dark:bg-yellow-800 text-yellow-900 dark:text-yellow-100",
    "reserved-in-cart": "bg-red-300 dark:bg-red-900 text-red-900 dark:text-red-100 opacity-80",
    "reserved-other": "bg-gray-300/70 dark:bg-zinc-700/70 text-gray-500 dark:text-zinc-300",
    unavailable: "bg-gray-200 dark:bg-zinc-800 text-gray-400 dark:text-zinc-500 opacity-40 cursor-not-allowed",
    available: "",
};

const OVERSCROLL = 5000;

export default function Basic({ onSelect, seatCategoryStyles, seatsFromApi, reservedSeats = [], canvasSize, scaleConfig, stage }) {

    //const seats = arena;
    //const seatMap = arena.map((s) => ({ // 1111
    const seatMap = useMemo(() => seatsFromApi.map((s) => ({
        id: s.id,
        import_key: s.import_key,
        label: s.label,
        row: Number(s.row),
        col: Number(s.col ?? 0),
        category: s.category,
        isSpacer: s.isSpacer,
        isVisibleSpacer: s.isVisibleSpacer ?? false,
        name: s.name,
        angle: Number(s.angle ?? 0),
        wheelchairAccessible: Boolean(s.wheelchairAccessible),
    })), [seatsFromApi]);


    const statusBySeatId = useMemo(() => {
        const m = new Map();
        for (const r of reservedSeats) {
            if (r.status === "mine") m.set(r.seatId, "reserved-mine");
            else if (r.status === "other") m.set(r.seatId, "reserved-other");
            else if (r.status === "in_cart") m.set(r.seatId, "reserved-in-cart");
            else if (r.status === "redeemed") m.set(r.seatId, "reserved-other");
            else if (r.status === "swapped_redeemed") m.set(r.seatId, "reserved-other");
        }
        return m;
    }, [reservedSeats]);

    const redeemedSeatIds = useMemo(() => {
        const s = new Set();
        for (const r of reservedSeats) {
            if (r.status === "redeemed" || r.status === "swapped_redeemed") {
                s.add(r.seatId);
            }
        }
        return s;
    }, [reservedSeats]);

    //console.log("🪑 Bejövő seatStocks:", seatStocks);

    const SHOW_PIVOT = process.env.NODE_ENV === "developmente";

    useEffect(() => {
        if (process.env.NODE_ENV !== "developmente") return;

        const posMap = new Map();
        for (const s of seatMap) {
            const k = `${s.angle}:${s.row}:${s.col}`;
            posMap.set(k, (posMap.get(k) || 0) + 1);
        }

        console.log("DUP POS", [...posMap.entries()].filter(([, c]) => c > 1));
    }, [seatMap]);

    const seatsByAngle = useMemo(() => {
        const m = new Map();
        for (const s of seatMap) {
            const a = s.angle ?? 0;
            const arr = m.get(a);
            if (arr) arr.push(s);
            else m.set(a, [s]);
        }
        return m;
    }, [seatMap]);

    const angles = useMemo(() => Array.from(seatsByAngle.keys()), [seatsByAngle]);

    // 🎭 PÉLDA SZÍNPAD
    const STAGE = stage ?? null;

    const categoryStyleMap = useMemo(() => {
        const fallback = {
            vip: { backgroundImage: "linear-gradient(to right, #14b8a6, #34d399, #0e7490)", color: "#fff" },
            premium: { backgroundColor: "#6D28D9", color: "#fff" },
            first: { backgroundColor: "#4F46E5", color: "#fff" },
            second: { backgroundColor: "#FFE4E6", color: "#222" },
            third: { backgroundColor: "#22C55E", color: "#222" },
            normal: { backgroundColor: "#38BDF8", color: "#222" },
            stairs: { backgroundColor: "#D1D5DB", color: "#222" },
            default: { backgroundColor: "#86EFAC", color: "#14532d" },
        };

        // custom nyer, fallback ha nincs
        return { ...fallback, ...(seatCategoryStyles ?? {}) };

    }, [seatCategoryStyles]);

    const SEAT_WHEELCHAIR_OVERLAY = (
        <Accessibility className="absolute bottom-1 right-1 w-4 h-4 text-white z-0 pointer-events-none" />
    );

    // Kijelzőméret alapján állítjuk be a kezdeti térkép nagyítását

    const DEFAULT_SCALE_CONFIG = [
        { maxWidth: 480, scale: 0.05 },
        { maxWidth: 640, scale: 0.05 },
        { maxWidth: 768, scale: 0.05 },
        { maxWidth: 1024, scale: 0.05 },
        { maxWidth: 1440, scale: 0.05 },
        { maxWidth: Infinity, scale: 0.2 },
    ];

    const [initialScale, setInitialScale] = useState(null);

    useEffect(() => {
        const config = scaleConfig ?? DEFAULT_SCALE_CONFIG;

        const updateScale = () => {
            const width = window.innerWidth;

            const matched = config.find(c => width < c.maxWidth);

            if (matched) {
                setInitialScale(matched.scale);
            }
        };

        updateScale(); // első rendernél
        window.addEventListener("resize", updateScale); // újraszámolás ablakméret-változásra
        return () => window.removeEventListener("resize", updateScale); // cleanup
    }, [scaleConfig]);



    // VÉGE
    const isTouch = useIsTouchDevice();

    // Húzás közben ne jelöljünk ki ülést

    const { preventClickRef, onPointerDown, onPointerUp } = useDragPreventClick();

    const onSeatMapClick = useCallback(
        (e) => {
            const el = e.target.closest("[data-seat-id]");
            if (!el) return;

            if (preventClickRef.current) return;
            if (el.dataset.selectable !== "1") return;

            onSelect(Number(el.dataset.seatId));
        },
        [onSelect]
    );

    const stageFontSize = STAGE?.labelFontSize ?? 20;

    return (
        <div className="overflow-hidden">
            {/* Ülőhelytérkép konténer */}
            <div className="h-[80vh] md:h-screen 2xl:h-[60vh] max-w-full flex items-center justify-center overflow-hidden">
                {/* TransformWrapper és tartalom... */}
                {initialScale !== null && (
                    <TransformWrapper
                        initialScale={initialScale}
                        minScale={0.04}
                        maxScale={4}
                        centerOnInit
                        doubleClick={{ disabled: true }}
                        zoomAnimation={{ disabled: true }}
                        limitToBounds={true}
                        disablePadding={false}
                        wheel={{
                            wheelDisabled: true,
                            activationKeys: ['Control', 'Meta', 'Alt'], // ⬅️ csak Ctrl-ra engedélyezi a zoomot
                        }}
                        panning={{
                            disabled: isTouch,             // alapból tiltsuk
                            touch: "two-finger",        // csak két ujjal lehessen panning
                            velocityDisabled: true
                        }}
                    >

                        <div
                            className="relative w-full h-full"
                        >
                            <TransformComponent wrapperStyle={{ width: "100%", height: "100%", touchAction: "pan-y", }}>
                                <div className="flex items-start justify-center">

                                    <div
                                        className="relative"
                                        style={{
                                            width: canvasSize.width + OVERSCROLL * 2,
                                            height: canvasSize.height + OVERSCROLL * 2,
                                        }}
                                    >

                                        <div
                                            className="absolute inline-block border rounded bg-gray-50 dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
                                            style={{
                                                left: OVERSCROLL,
                                                top: OVERSCROLL,
                                                width: canvasSize.width,
                                                height: canvasSize.height,
                                            }}
                                            onPointerDown={onPointerDown}
                                            onPointerUp={onPointerUp}
                                            onClick={onSeatMapClick}
                                        >

                                            {/* 🎭 SZÍNPAD (példa) */}
                                            {STAGE && (
                                                <div
                                                    className="
        absolute flex items-center justify-center
        bg-gray-800 dark:bg-zinc-700
        text-white font-bold
        shadow-lg
        pointer-events-none
    "
                                                    style={{
                                                        left: STAGE.x === 0 ? "50%" : STAGE.x,
                                                        top: STAGE.y,
                                                        width: STAGE.width,
                                                        height: STAGE.height,
                                                        fontSize: stageFontSize,
                                                        borderRadius: STAGE.shape === "circle" ? "50%" : "8px",
                                                        transform: `
            translateX(${STAGE.x === 0 ? "-50%" : "0"})
            rotate(${STAGE.rotation}deg)
        `,
                                                        zIndex: 10,
                                                    }}
                                                >
                                                    {STAGE.label}
                                                </div>
                                            )}


                                            {/* 💺 ÚJ RENDER – csak a forgatott / absolute seatmap */}
                                            {angles.map(angle => {
                                                const seatsForAngle = seatsByAngle.get(angle) ?? [];

                                                if (seatsForAngle.length === 0) return null;

                                                return (
                                                    <div
                                                        key={angle}
                                                        className="absolute left-1/2 top-1/2"
                                                        style={{ transform: "translate(-50%, -50%)" }}
                                                    >

                                                        {/* 🔴 PIROS PÖTTY a forgatási középpontban */}
                                                        {SHOW_PIVOT &&
                                                            <div
                                                                style={{
                                                                    position: "absolute",
                                                                    width: "1px",
                                                                    height: "1px",
                                                                    background: "red",
                                                                    borderRadius: "50%",
                                                                    transform: "translate(-50%, -50%)",
                                                                    zIndex: 9999,
                                                                    pointerEvents: "none",
                                                                    left: "50%",
                                                                    top: "50%",
                                                                }}
                                                            />
                                                        }

                                                        {/* 💫 csak itt történik a FORGATÁS */}
                                                        <div style={{ transform: `rotate(${angle}deg)` }}>
                                                            <div className="relative"
                                                                style={{
                                                                    left: "50%",
                                                                    transform: "translateX(-50%)"
                                                                }}
                                                            >

                                                                {seatsForAngle.map((seat) => {
                                                                    const isLabel = seat.isVisibleSpacer;
                                                                    const isUnavailable = seat.category === "unavailable";

                                                                    const status = isLabel
                                                                        ? "label"
                                                                        : isUnavailable
                                                                            ? "unavailable"
                                                                            : (statusBySeatId.get(seat.id) ?? "available");

                                                                    const isSelectable = !isLabel && !isUnavailable;

                                                                    const ROW_HEIGHT = 45;
                                                                    const seatOffsetRows = stage?.seatOffsetRows ?? 0;

                                                                    if (isLabel) {
                                                                        return (
                                                                            <div
                                                                                key={seat.id}
                                                                                style={{
                                                                                    position: "absolute",
                                                                                    left: seat.col * 40 - 16,
                                                                                    top: (seat.row - 1 - seatOffsetRows) * ROW_HEIGHT,
                                                                                }}
                                                                                className="
          w-8 h-8 flex items-center justify-center
          text-xs font-semibold
          text-black dark:text-white
          select-none pointer-events-none
        "
                                                                            >
                                                                                {seat.label}
                                                                            </div>
                                                                        );
                                                                    }

                                                                    const x = seat.col * 40 - 16;
                                                                    const y = (seat.row - 1 - seatOffsetRows) * ROW_HEIGHT;
                                                                    const catStyle = categoryStyleMap[seat.category] ?? categoryStyleMap.default;

                                                                    return (
                                                                        <div
                                                                            key={seat.id}
                                                                            role="button"
                                                                            tabIndex={isSelectable ? 0 : -1}
                                                                            aria-disabled={!isSelectable}
                                                                            onKeyDown={(e) => {
                                                                                if (!isSelectable) return;
                                                                                if (e.key === "Enter" || e.key === " ") {
                                                                                    e.preventDefault();
                                                                                    if (!preventClickRef.current) onSelect(seat.id);
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                position: "absolute",
                                                                                left: x,
                                                                                top: y,
                                                                                ...(status === "available" ? catStyle : {})
                                                                            }}
                                                                            data-seat-id={seat.id}
                                                                            data-selectable={isSelectable ? "1" : "0"}
                                                                            className={`
  w-8 h-8 text-xs font-medium border flex items-center justify-center
  ${STATUS_CLASS[status] ?? "bg-white dark:bg-zinc-800 text-gray-900 dark:text-gray-100"}
  ${redeemedSeatIds.has(seat.id) ? "border-red-300 border-2" : ""}
  select-none
`}
                                                                        >
                                                                            {seat.wheelchairAccessible && SEAT_WHEELCHAIR_OVERLAY}
                                                                            {seat.label}
                                                                        </div>
                                                                    );
                                                                })}

                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}



                                        </div>

                                    </div>

                                </div>
                            </TransformComponent>
                        </div>
                    </TransformWrapper>
                )}
            </div>
        </div>
    );
}